// Client-side store for the waitlist queue-position tracking token.
//
// At signup (OTP verify JSON response, or the `?pt=` param on the X-join
// redirect) the backend hands the browser a signed token keyed to the
// signup row. We stash it (plus the position captured at signup) so a
// returning user can re-read their LIVE position via
// `useWaitlistPosition({ token })` — no email re-entry, no enumeration.
//
// Not an access grant: holding this never reveals the app (only a valid
// access code does). The network call lives in the gate (SDK hook).

import { logger } from '@/lib/telemetry/logger';

const QUEUE_TOKEN_KEY = 'morfi_waitlist_queue_token';
const QUEUE_JOINED_POSITION_KEY = 'morfi_waitlist_queue_joined_pos';

export interface StoredQueueToken {
  token: string;
  /** Position captured at signup — drives the "moved up N" chip. */
  joinedPosition: number;
}

/** Persist the tracking token + the position captured at signup. */
export function storeQueueToken(token: string, joinedPosition: number): void {
  if (typeof window === 'undefined' || token.length === 0) {
    return;
  }
  try {
    localStorage.setItem(QUEUE_TOKEN_KEY, token);
    localStorage.setItem(QUEUE_JOINED_POSITION_KEY, String(joinedPosition));
  } catch (err: unknown) {
    logger.warn('localStorage write failed for queue token', {
      error: err instanceof Error ? err.message.slice(0, 200) : String(err),
    });
  }
}

/** Forget the stored signup token + position. Called on sign-out so the gate
 *  no longer restores the previous account's "You're on the list" card (which
 *  would otherwise leak that user's waitlist identity on the public gate). */
export function clearQueueToken(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    localStorage.removeItem(QUEUE_TOKEN_KEY);
    localStorage.removeItem(QUEUE_JOINED_POSITION_KEY);
  } catch (err: unknown) {
    logger.warn('localStorage remove failed for queue token', {
      error: err instanceof Error ? err.message.slice(0, 200) : String(err),
    });
  }
}

/** The stored token + joined position, or null if the browser holds none. */
export function getQueueToken(): StoredQueueToken | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const token = localStorage.getItem(QUEUE_TOKEN_KEY);
    if (token === null || token.length === 0) {
      return null;
    }
    const rawPos = localStorage.getItem(QUEUE_JOINED_POSITION_KEY);
    const joinedPosition = rawPos !== null && Number.isFinite(Number(rawPos)) ? Number(rawPos) : 0;
    return { token, joinedPosition };
  } catch (err: unknown) {
    logger.warn('localStorage read failed for queue token', {
      error: err instanceof Error ? err.message.slice(0, 200) : String(err),
    });
    return null;
  }
}
